import { addDaysYmd } from "./heuteScheduleBuckets";
import { todayYmdZurich } from "./schoolAttention";

const MAX_CALENDAR_LINES = 4;

/** @param {string} ymd */
export function formatCalendarWeekday(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return "—";
  const date = new Date(`${ymd}T12:00:00`);
  return date.toLocaleDateString("de-CH", { weekday: "long" });
}

/** @param {string} ymd */
export function formatCalendarDayMonth(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return "—";
  return `${d}.${String(m).padStart(2, "0")}`;
}

/** @param {string} ymd */
export function formatCalendarDayWithWeekday(ymd) {
  return `${formatCalendarWeekday(ymd)}, ${formatCalendarDayMonth(ymd)}`;
}

/** @param {string} todayYmd */
export function formatCalendarTodayLine(todayYmd) {
  return `Heute, ${formatCalendarDayWithWeekday(todayYmd)}.`;
}

/**
 * @param {{ startsOn?: string, endsOn?: string }} event
 */
export function formatCalendarDateRange(event) {
  if (!event?.startsOn) return "—";
  if (event.startsOn === event.endsOn) return formatCalendarDayWithWeekday(event.startsOn);
  return `${formatCalendarDayWithWeekday(event.startsOn)} – ${formatCalendarDayWithWeekday(event.endsOn)}`;
}

/**
 * @param {{ startsOn?: string, endsOn?: string }} event
 * @param {string} todayYmd
 */
export function eventActiveOn(event, todayYmd) {
  if (!event?.startsOn || !event?.endsOn) return false;
  return todayYmd >= event.startsOn && todayYmd <= event.endsOn;
}

/**
 * @param {{ startsOn?: string, endsOn?: string }} a
 * @param {{ startsOn?: string, endsOn?: string }} b
 */
export function calendarRangesOverlap(a, b) {
  if (!a?.startsOn || !a?.endsOn || !b?.startsOn || !b?.endsOn) return false;
  return a.startsOn <= b.endsOn && b.startsOn <= a.endsOn;
}

/**
 * @param {object} event
 */
function calendarEventMergeKey(event) {
  const name = String(event.title || event.name || "").trim().toLowerCase();
  return `${event.kind}:${name}`;
}

/**
 * Merge overlapping events with the same kind + name into one row (union of ranges).
 * @param {object[]} events
 */
export function mergeOverlappingCalendarEvents(events) {
  const groups = new Map();

  for (const event of events || []) {
    const key = calendarEventMergeKey(event);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(event);
  }

  const merged = [];

  for (const group of groups.values()) {
    const sorted = [...group].sort((a, b) =>
      String(a.startsOn).localeCompare(String(b.startsOn)),
    );
    let current = { ...sorted[0] };

    for (let i = 1; i < sorted.length; i += 1) {
      const next = sorted[i];
      if (calendarRangesOverlap(current, next)) {
        if (next.startsOn < current.startsOn) current.startsOn = next.startsOn;
        if (next.endsOn > current.endsOn) current.endsOn = next.endsOn;
      } else {
        merged.push(current);
        current = { ...next };
      }
    }
    merged.push(current);
  }

  return merged;
}

/**
 * @param {{ kind?: string, title?: string, name?: string, startsOn?: string, endsOn?: string }} event
 */
export function calendarEventKindLabel(event) {
  return event?.kind === "public_holiday" ? "Feiertag" : "Schulferien";
}

/**
 * @param {object} event
 * @param {string} [todayYmd]
 */
export function formatCalendarRecallLine(event, todayYmd = todayYmdZurich()) {
  const title = String(event.title || event.name || calendarEventKindLabel(event)).trim();
  const kindLabel = calendarEventKindLabel(event);
  const activeToday = eventActiveOn(event, todayYmd);

  if (activeToday) {
    if (event.startsOn === event.endsOn) {
      return `• ${kindLabel}: ${title}`;
    }
    return `• ${kindLabel}: ${title} (${formatCalendarDateRange(event)})`;
  }

  if (event.startsOn === event.endsOn) {
    return `• ${formatCalendarDayWithWeekday(event.startsOn)} · ${kindLabel}: ${title}`;
  }
  return `• ${formatCalendarDateRange(event)} · ${kindLabel}: ${title}`;
}

/**
 * @param {{
 *   upcomingHolidays?: object[],
 *   imminent?: object[],
 *   nextPublicHoliday?: object|null,
 *   nextSchoolHoliday?: object|null,
 * } | null} calendar
 * @param {{ days?: number, todayYmd?: string }} [opts]
 * @returns {{ todayLine: string, lines: string[] }}
 */
export function buildCalendarRecallLines(calendar, opts = {}) {
  const days = opts.days ?? 14;
  const todayYmd = opts.todayYmd ?? todayYmdZurich();
  const maxDate = addDaysYmd(todayYmd, days);
  const todayLine = formatCalendarTodayLine(todayYmd);

  if (!calendar) {
    return { todayLine, lines: [] };
  }

  const seen = new Set();
  const merged = [];

  const add = (event) => {
    if (!event?.startsOn) return;
    if (event.startsOn > maxDate) return;
    if (event.endsOn < todayYmd) return;
    const key = `${event.kind}:${event.startsOn}:${event.endsOn}:${event.title || event.name}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(event);
  };

  for (const event of calendar.upcomingHolidays || []) add(event);
  for (const event of calendar.imminent || []) add(event);
  add(calendar.nextPublicHoliday);
  add(calendar.nextSchoolHoliday);

  const collapsed = mergeOverlappingCalendarEvents(merged);

  collapsed.sort((a, b) => {
    const aActive = eventActiveOn(a, todayYmd) ? 0 : 1;
    const bActive = eventActiveOn(b, todayYmd) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    return String(a.startsOn).localeCompare(String(b.startsOn));
  });

  return {
    todayLine,
    lines: collapsed
      .slice(0, MAX_CALENDAR_LINES)
      .map((event) => formatCalendarRecallLine(event, todayYmd)),
  };
}
